import { array, fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import _deepEqual from 'lodash/isEqual';
import BvCard from 'volta/components/bv-card';
import BvChoiceBlocks from 'volta/components/bv-choice-blocks';
import BvMenu from 'volta/components/bv-menu';
import BvPopover from 'volta/components/bv-popover';
import BvTag from 'volta/components/bv-tag';
import BvText from 'volta/components/bv-text';
import BvToggle from 'volta/components/bv-toggle';
import PoWorkbenchDateViewSelect from 'volta/components/po-workbench/date-view-select';
import { DefaultSettings } from 'volta/services/collection-builder-po-groups';

import type { EmberChangeset } from 'ember-changeset';
import type {
  ICollectionViewEditCmd,
  IPOWorkbenchCollectionArgs,
  TCodeDescMode
} from 'volta/models/types/collection-view';
import type { TDateField } from 'volta/models/types/planning-order';

interface PoWorkbenchCollectionViewArgsEditorSignature {
  Args: {
    changeset: EmberChangeset & ICollectionViewEditCmd;
    settings: IPOWorkbenchCollectionArgs;
    metadata?: { loading: boolean; disabled: boolean };
    onChange?: (settings: IPOWorkbenchCollectionArgs) => void;
  };
}

export default class PoWorkbenchCollectionViewArgsEditorComponent extends Component<PoWorkbenchCollectionViewArgsEditorSignature> {
  get settings(): IPOWorkbenchCollectionArgs {
    const { settings } = this.args;
    return {
      showCapacity: settings.showCapacity ?? DefaultSettings.showCapacity,
      showCapacityChart: settings.showCapacityChart ?? DefaultSettings.showCapacityChart,
      dateField: settings.dateField ?? DefaultSettings.dateField,
      codeDescMode: settings.codeDescMode ?? DefaultSettings.codeDescMode,
      showSkuDesc: settings.showSkuDesc ?? DefaultSettings.showSkuDesc
    };
  }

  get showCode() {
    const { codeDescMode = ['desc'] } = this.args.settings;
    return codeDescMode.includes('code');
  }

  get showDesc() {
    const { codeDescMode = ['desc'] } = this.args.settings;
    return codeDescMode.includes('desc');
  }

  get isTimeline() {
    return this.args.changeset.collectionType === 'TIMELINE';
  }

  get hasWorkshopInGroup() {
    return this.args.changeset.groupBy?.includes('workshop');
  }

  @action
  handleDateFieldChange(dateField: TDateField) {
    this.args.onChange?.({ ...this.settings, dateField });
  }

  @action
  handleShowCapacityChange(showCapacity: boolean) {
    this.args.onChange?.({
      ...this.settings,
      showCapacity
    });
  }

  @action
  handleShowCapacityChartChange(showCapacityChart: boolean) {
    this.args.onChange?.({
      ...this.settings,
      showCapacityChart
    });
  }

  @action
  handleShowSkuDescChange(showSkuDesc: boolean) {
    this.args.onChange?.({ ...this.settings, showSkuDesc });
  }

  @action
  handleCodeDescChange(codeDescMode: TCodeDescMode[]) {
    this.args.onChange?.({ ...this.settings, codeDescMode });
  }

  cdmChecked = (value: TCodeDescMode[]) => {
    return _deepEqual(value, this.settings.codeDescMode);
  };

  <template>
    <BvCard class='u-p-2'>
      <BvMenu as |MenuSection|>
        <MenuSection as |MenuItem|>
          <MenuItem>
            <:default>
              <BvText @text={{t 'dateField'}} />
            </:default>
            <:suffix>
              <PoWorkbenchDateViewSelect
                style={{htmlSafe 'margin-top: -0.5rem; margin-bottom: -0.5rem'}}
                @selectedDateField={{this.settings.dateField}}
                @onChange={{this.handleDateFieldChange}}
                @isLoading={{@metadata.loading}}
                @disabled={{@metadata.disabled}}
                @size='sm'
              />
            </:suffix>
          </MenuItem>
          <MenuItem @onClick={{fn this.handleShowCapacityChange (not this.settings.showCapacity)}}>
            <:default>
              <BvText @text={{t 'poWorkbench.showWorkshopCapacity'}} />
            </:default>
            <:suffix>
              <BvToggle
                @onToggle={{this.handleShowCapacityChange}}
                @toggled={{this.settings.showCapacity}}
                @size='sm'
              />
            </:suffix>
          </MenuItem>
          {{#if this.settings.showCapacity}}
            <MenuItem
              @onClick={{fn
                this.handleShowCapacityChartChange
                (not this.settings.showCapacityChart)
              }}
            >
              <:default>
                <BvText @text={{t 'poWorkbench.showCapacityChart'}} />
              </:default>
              <:suffix>
                <BvToggle
                  @onToggle={{this.handleShowCapacityChartChange}}
                  @toggled={{this.settings.showCapacityChart}}
                  @size='sm'
                />
              </:suffix>
            </MenuItem>
          {{/if}}
          <MenuItem @onClick={{fn this.handleShowSkuDescChange (not this.settings.showSkuDesc)}}>
            <:default>
              <BvText @text={{t 'poWorkbench.showSkuDesc'}} />
            </:default>
            <:suffix>
              <BvToggle
                @onToggle={{this.handleShowSkuDescChange}}
                @toggled={{this.settings.showSkuDesc}}
                @size='sm'
              />
            </:suffix>
          </MenuItem>
          <BvPopover @horizontalPosition='right' @size='md' as |popover|>
            <MenuItem @hasArrow={{true}} @onClick={{popover.toggle}} {{popover.trigger}}>
              <BvText @text={{t 'poWorkbench.fieldDisplayMode'}} />
            </MenuItem>
            <popover.Content>
              <popover.Header>
                <BvTag>
                  {{#if this.showCode}}
                    <BvText @text={{t 'code'}} @fontWeight='semibold' />
                  {{/if}}
                  {{#if (and this.showCode this.showDesc)}}
                    <BvText @text='•' class='u-opacity-disabled' @fontWeight='regular' />
                  {{/if}}
                  {{#if this.showDesc}}
                    <BvText
                      @text={{t 'description'}}
                      @fontWeight={{if this.showCode 'regular' 'semibold'}}
                    />
                  {{/if}}
                </BvTag>
              </popover.Header>
              <popover.Body>
                <BvChoiceBlocks
                  @items={{array
                    (hash value=(array 'code') description=(t 'code'))
                    (hash value=(array 'desc') description=(t 'desc'))
                    (hash value=(array 'code' 'desc') description=(t 'both'))
                  }}
                  @onChange={{this.handleCodeDescChange}}
                  @checked={{this.settings.codeDescMode}}
                  @auto={{true}}
                  @maxColumns='3'
                  @size='sm'
                />
              </popover.Body>
            </popover.Content>
          </BvPopover>
        </MenuSection>
      </BvMenu>
    </BvCard>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'PoWorkbench::CollectionViewArgsEditor': typeof PoWorkbenchCollectionViewArgsEditorComponent;
    'po-workbench/collection-view-args-editor': typeof PoWorkbenchCollectionViewArgsEditorComponent;
  }
}
